/* Pasanka motion layer.
   Restrained, purposeful motion: gentle smooth scroll, scroll progress, heading reveals below the fold,
   light parallax, count-up stats and staggered product grids.

   Progressive enhancement: the storefront works without this file. It stays off for reduced-motion,
   save-data and the theme editor. Libraries load from CDNs; if one fails the rest still run. */
(function () {
  'use strict';

  var doc = document, root = doc.documentElement;
  var mq = function (q) { return !!(window.matchMedia && window.matchMedia(q).matches); };

  if (mq('(prefers-reduced-motion: reduce)')) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (navigator.connection && navigator.connection.saveData) return;

  var SRC = {
    gsap: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js',
    st: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js',
    lenis: 'https://cdn.jsdelivr.net/npm/lenis@1.1.18/dist/lenis.min.js'
  };

  var fine = mq('(hover: hover) and (pointer: fine)');
  var small = mq('(max-width: 767px)');

  var $ = function (sel, scope) { return (scope || doc).querySelector(sel); };
  var $$ = function (sel, scope) { return Array.prototype.slice.call((scope || doc).querySelectorAll(sel)); };
  var guard = function (fn) { try { fn(); } catch (e) { if (window.console) console.warn('[motion]', e); } };
  var inView = function (el, frac) { return el.getBoundingClientRect().top < window.innerHeight * (frac || 0.95); };

  var scripts = {};
  function loadScript(src) {
    if (scripts[src]) return scripts[src];
    scripts[src] = new Promise(function (resolve, reject) {
      var s = doc.createElement('script');
      s.src = src; s.async = true; s.onload = resolve; s.onerror = reject;
      doc.head.appendChild(s);
    });
    return scripts[src];
  }

  /* ------------------------------------------------------------ smooth scroll */
  // Light touch: close to native feel, just softer. Touch devices keep native scrolling.
  function smooth() {
    if (!window.Lenis) return;
    var lenis = new window.Lenis({
      lerp: 0.12,
      smoothWheel: true,
      prevent: function (node) {
        return !!(node.closest && node.closest('[data-cart-drawer], .pas-modal, .pas-select-list, [data-search-results], [data-mobile-menu], textarea, select'));
      }
    });
    lenis.on('scroll', window.ScrollTrigger.update);
    window.gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    window.gsap.ticker.lagSmoothing(0);

    // Cart drawer / menus lock page scroll via overflow on <html>.
    new MutationObserver(function () {
      if (root.style.overflow === 'hidden') lenis.stop(); else lenis.start();
    }).observe(root, { attributes: true, attributeFilter: ['style'] });

    doc.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a || e.defaultPrevented) return;
      var id = a.getAttribute('href');
      if (id.length < 2) return;
      var target = $(id);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -96 });
    });
  }

  /* ------------------------------------------- progress bar + header shadow */
  function chrome() {
    var bar = doc.createElement('div');
    bar.className = 'motion-progress';
    bar.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(bar);
    var header = $('[data-site-header]');
    window.ScrollTrigger.create({
      start: 0, end: 'max',
      onUpdate: function (self) {
        bar.style.transform = 'scaleX(' + self.progress + ')';
        if (header) header.classList.toggle('is-scrolled', self.scroll() > 40);
      }
    });
  }

  /* ------------------------------------------- section headings (below fold) */
  function wrapWord(textNode) {
    var outer = doc.createElement('span'); outer.className = 'mw'; outer.setAttribute('aria-hidden', 'true');
    var inner = doc.createElement('span'); inner.className = 'mw-i';
    if (textNode) inner.appendChild(textNode);
    outer.appendChild(inner);
    return { outer: outer, inner: inner };
  }
  function splitWords(el) {
    var words = [];
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (ch) {
        if (ch.nodeType === 3) {
          var frag = doc.createDocumentFragment();
          ch.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(doc.createTextNode(' ')); return; }
            var w = wrapWord(doc.createTextNode(part));
            words.push(w.inner); frag.appendChild(w.outer);
          });
          node.replaceChild(frag, ch);
        } else if (ch.nodeType === 1 && ch.tagName !== 'BR') {
          if (ch.classList.contains('gradient-text')) {          // keep accent spans whole (background-clip:text)
            var w2 = wrapWord(null);
            node.replaceChild(w2.outer, ch);
            w2.inner.appendChild(ch);
            words.push(w2.inner);
          } else walk(ch);
        }
      });
    })(el);
    return words;
  }
  function headings() {
    var gsap = window.gsap;
    // Only h2s that start below the fold: the hero already has its own entrance, so nothing flashes.
    $$('#MainContent h2').forEach(function (h) {
      if (h.dataset.split || h.closest('.pas-modal, .rte, [data-no-split], [data-hero], [data-hero-home]')) return;
      if (!h.textContent.trim() || inView(h, 1.05)) return;
      h.setAttribute('aria-label', h.textContent.replace(/\s+/g, ' ').trim());
      var words = splitWords(h);
      h.dataset.split = '1';
      if (!words.length) return;
      gsap.set(words, { yPercent: 105 });
      window.ScrollTrigger.create({
        trigger: h, start: 'top 88%', once: true,
        onEnter: function () { gsap.to(words, { yPercent: 0, duration: 0.9, ease: 'power3.out', stagger: 0.035 }); }
      });
    });
  }

  /* ---------------------------------------------------------------- parallax */
  function parallax() {
    var gsap = window.gsap;
    $$('section[data-hero] > div.absolute img').forEach(function (img) {
      gsap.fromTo(img, { yPercent: 0, scale: 1.12 }, {
        yPercent: 6, scale: 1.12, ease: 'none',
        scrollTrigger: { trigger: img.closest('section'), start: 'top top', end: 'bottom top', scrub: true }
      });
    });
    if (small) return;
    $$('#MainContent .focus-img img, #MainContent .lift img').forEach(function (img) {
      if (/scale-105/.test(img.className) || img.closest('[data-product-card], [data-carousel], .pas-modal') || !img.parentElement) return;
      gsap.fromTo(img, { yPercent: -4, scale: 1.1 }, {
        yPercent: 4, scale: 1.1, ease: 'none',
        scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: 0.5 }
      });
    });
  }

  /* ---------------------------------------------------------------- counters */
  function counters() {
    var gsap = window.gsap;
    $$('.about-stats .font-display, [data-count]').forEach(function (el) {
      var m = el.textContent.trim().match(/^([^0-9]*)([\d][\d,]*(?:\.\d+)?)(.*)$/);
      if (!m) return;
      var target = parseFloat(m[2].replace(/,/g, ''));
      if (!isFinite(target) || inView(el, 0.8)) return;     // already visible: leave the number alone
      var comma = m[2].indexOf(',') > -1, dec = (m[2].split('.')[1] || '').length;
      var obj = { v: 0 };
      var fmt = function (v) {
        var s = v.toFixed(dec);
        if (comma) s = Number(s).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
        return m[1] + s + m[3];
      };
      el.setAttribute('aria-label', el.textContent.trim());
      el.textContent = fmt(0);
      window.ScrollTrigger.create({
        trigger: el, start: 'top 88%', once: true,
        onEnter: function () {
          gsap.to(obj, { v: target, duration: 1.8, ease: 'power2.out', onUpdate: function () { el.textContent = fmt(obj.v); }, onComplete: function () { el.textContent = fmt(target); } });
        }
      });
    });
  }

  /* -------------------------------------- product grids: staggered fade-up */
  function cards() {
    if (!('IntersectionObserver' in window)) return;
    var list = $$('#MainContent [data-product-card]').filter(function (c) {
      return !c.closest('[data-carousel]') && !inView(c, 1);
    });
    if (!list.length) return;
    list.forEach(function (c) { c.classList.add('m-card'); });
    var queue = [], timer = 0;
    var flush = function () {
      queue.forEach(function (c, i) { setTimeout(function () { c.classList.add('m-in'); }, i * 70); });
      queue = []; timer = 0;
    };
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target); queue.push(en.target);
      });
      if (queue.length && !timer) timer = setTimeout(flush, 30);
    }, { rootMargin: '0px 0px -6% 0px' });
    list.forEach(function (c) { io.observe(c); });
  }

  /* ------------------------------------------------- magnetic call-to-actions */
  function magnetic() {
    if (!fine) return;
    var gsap = window.gsap;
    $$('a.btn-magnetic').forEach(function (el) {
      var xTo = gsap.quickTo(el, 'x', { duration: 0.5, ease: 'power3' });
      var yTo = gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3' });
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * 0.12);
        yTo((e.clientY - (r.top + r.height / 2)) * 0.18);
      });
      el.addEventListener('pointerleave', function () { xTo(0); yTo(0); });
    });
  }

  /* ---------------------------------------------------------------- Three.js */
  function webgl() {
    try {
      var c = doc.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
  }

  var leafTextures = null;
  function loadLeafTextures() {
    if (leafTextures) return leafTextures;
    var THREE = window.THREE;
    var urls = (window.theme && window.theme.motion && window.theme.motion.leaves) || [];
    var loader = new THREE.TextureLoader();
    leafTextures = Promise.all(urls.map(function (u) {
      return new Promise(function (resolve) {
        loader.load(u, function (t) { t.encoding = THREE.sRGBEncoding; t.anisotropy = 4; resolve(t); }, undefined, function () { resolve(null); });
      });
    })).then(function (list) { return list.filter(Boolean); });
    return leafTextures;
  }

  // A leaf is a gently cupped, slightly folded plane carrying a real photograph.
  function leafGeometry(w, h) {
    var THREE = window.THREE;
    var g = new THREE.PlaneGeometry(w, h, 6, 12);
    var p = g.attributes.position;
    for (var i = 0; i < p.count; i++) {
      var nx = p.getX(i) / (w / 2), ny = p.getY(i) / (h / 2);
      p.setZ(i, -0.16 * w * nx * nx - 0.05 * w * Math.abs(nx) + 0.05 * h * Math.sin(ny * 2.1));
    }
    g.computeVertexNormals();
    return g;
  }

  function buildScene(host, textures) {
    var THREE = window.THREE;
    var tone = host.dataset.threeTone || 'dark';

    var canvas = doc.createElement('canvas');
    canvas.className = 'three-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    host.appendChild(canvas);

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    renderer.setClearColor(0x000000, 0);
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = tone === 'light' ? 1.05 : 0.95;

    var scene = new THREE.Scene();
    scene.fog = new THREE.Fog(tone === 'light' ? 0xf1f7f2 : 0x022a1d, 11, 27);   // distant leaves recede into the page
    var camera = new THREE.PerspectiveCamera(50, 1, 0.1, 60);
    camera.position.z = 14;

    scene.add(new THREE.HemisphereLight(0xfff4dc, 0x1b3a22, tone === 'light' ? 0.95 : 0.8));
    var sun = new THREE.DirectionalLight(0xffe2b0, tone === 'light' ? 1.15 : 1.35);
    sun.position.set(-5, 7, 7);
    scene.add(sun);

    var group = new THREE.Group();
    scene.add(group);

    var disposables = [];
    var order = [0, 4, 2, 0, 4, 3];                              // fresh green leaves lead; cinnamon is a quiet accent
    var count = small ? 3 : 6;
    var leaves = [];
    for (var i = 0; i < count; i++) {
      var tex = textures[order[i % order.length] % textures.length];
      var img = tex.image;
      var aspect = (img && img.width && img.height) ? img.width / img.height : 0.5;
      var h = 2.35 + Math.random() * 1.25;
      var w = h * aspect;
      var geo = leafGeometry(w, h);
      var mat = new THREE.MeshStandardMaterial({
        map: tex, side: THREE.DoubleSide, transparent: true, opacity: tone === 'light' ? 0.7 : 0.76,
        alphaTest: 0.35, depthWrite: false, roughness: 0.62, metalness: 0,
        emissive: new THREE.Color(0x586b2c), emissiveMap: tex, emissiveIntensity: tone === 'light' ? 0.18 : 0.28
      });
      disposables.push(geo, mat);
      var mesh = new THREE.Mesh(geo, mat);
      var side = Math.random() < 0.5 ? -1 : 1;
      // Keep leaves toward the edges so headings stay clear; upper-left only on the text side.
      var ny = side < 0 ? 0.04 + Math.random() * 0.4 : (Math.random() - 0.5) * 0.9;
      mesh.userData = {
        nx: side * (0.3 + Math.random() * 0.2), ny: ny, z: -4 + Math.random() * 7,
        rx: (Math.random() - 0.5) * 0.7, ry: (Math.random() - 0.5) * 0.8, rz: Math.random() * 6.28,
        f: [0.18 + Math.random() * 0.22, 0.2 + Math.random() * 0.25, 0.22 + Math.random() * 0.2, 0.15 + Math.random() * 0.2, 0.12 + Math.random() * 0.15],
        p: [Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28],
        ax: 0.25 + Math.random() * 0.35, ay: 0.2 + Math.random() * 0.3,
        born: 0.15 * i + Math.random() * 0.4, bx: 0, by: 0
      };
      mesh.scale.setScalar(0.001);
      group.add(mesh);
      leaves.push(mesh);
    }

    var mouse = { x: 0, y: 0 };
    var onMove = function (e) {
      var r = host.getBoundingClientRect();
      mouse.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
      mouse.y = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    if (fine) window.addEventListener('pointermove', onMove, { passive: true });

    var layout = function () {
      var visH = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.position.z;
      var visW = visH * camera.aspect;
      leaves.forEach(function (l) { l.userData.bx = l.userData.nx * visW; l.userData.by = l.userData.ny * visH; });
    };
    var resize = function () {
      var w = host.clientWidth || 1, h = host.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      layout();
    };
    resize();
    var ro = window.ResizeObserver ? new ResizeObserver(resize) : null;
    if (ro) ro.observe(host);

    var ease = function (x) { x = Math.max(0, Math.min(1, x)); return 1 - Math.pow(1 - x, 3); };
    var raf = 0, last = performance.now(), t = 0, running = false;
    var tick = function (now) {
      raf = requestAnimationFrame(tick);
      var dt = Math.min((now - last) / 1000, 0.05);
      last = now; t += dt;
      var wind = Math.sin(t * 0.33) * 0.35 + Math.sin(t * 0.81) * 0.12;
      leaves.forEach(function (l) {
        var u = l.userData;
        var depth = (u.z + 4) / 7;                                // 0 far .. 1 near
        l.position.x = u.bx + Math.sin(t * u.f[0] + u.p[0]) * u.ax + wind * (0.5 + depth) + mouse.x * (0.2 + depth) * 0.5;
        l.position.y = u.by + Math.sin(t * u.f[1] + u.p[1]) * u.ay - mouse.y * (0.1 + depth) * 0.3;
        l.position.z = u.z;
        l.rotation.set(
          u.rx + Math.sin(t * u.f[2] + u.p[2]) * 0.32,
          u.ry + Math.sin(t * u.f[3] + u.p[3]) * 0.4,
          u.rz + Math.sin(t * u.f[4] + u.p[4]) * 0.28
        );
        l.scale.setScalar(Math.max(0.001, ease((t - u.born) / 1.6)));
      });
      var rect = host.getBoundingClientRect();
      var p = Math.max(-1, Math.min(1, -(rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight));
      group.position.y += (p * 1.4 - group.position.y) * 0.06;
      renderer.render(scene, camera);
    };

    return {
      start: function () { if (running) return; running = true; last = performance.now(); raf = requestAnimationFrame(tick); },
      destroy: function () {
        running = false; cancelAnimationFrame(raf);
        window.removeEventListener('pointermove', onMove);
        if (ro) ro.disconnect();
        disposables.forEach(function (d) { if (d && d.dispose) d.dispose(); });
        renderer.dispose();
        if (renderer.forceContextLoss) renderer.forceContextLoss();
        canvas.remove();
      }
    };
  }

  function three() {
    if (weak || !webgl()) return;
    var hosts = $$('[data-three]');
    if (!hosts.length) return;
    loadScript(SRC.three).then(loadLeafTextures).then(function (textures) {
      if (!textures.length) return;
      hosts.forEach(function (host) {
        if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
        host.classList.add('three-host');
        var scene = null;
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { if (!scene) scene = buildScene(host, textures); scene.start(); }
            else if (scene) { scene.destroy(); scene = null; }
          });
        }, { rootMargin: '100px' }).observe(host);
      });
    }).catch(function () {});
  }

  /* -------------------------------------------------------------------- boot */
  function setup() {
    window.gsap.registerPlugin(window.ScrollTrigger);
    root.classList.add('motion');
    guard(smooth);
    guard(chrome);
    guard(headings);
    guard(parallax);
    guard(counters);
    guard(cards);
    guard(magnetic);
    var refresh = function () { window.ScrollTrigger.refresh(); };
    window.addEventListener('load', refresh);
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(refresh);
  }

  var lenisLoad = loadScript(SRC.lenis).catch(function () {});
  loadScript(SRC.gsap)
    .then(function () { return loadScript(SRC.st); })
    .then(function () { return lenisLoad; })
    .then(setup)
    .catch(function () {});
})();
